import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import RefreshIcon from '@mui/icons-material/Refresh';
import BusinessIcon from '@mui/icons-material/Business';
import StorefrontIcon from '@mui/icons-material/Storefront';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { API_BASE_URL } from '../../../utils/config';
import ConfirmDialog from '../../shared/ConfirmDialog';
import { notifySuccess, notifyError } from '../../../helper/notify';
import { toUsaDayjs } from '../../../helper/libs/usa-time';
import { useSellviaCleanupJob } from '../../../context/SellviaCleanupJobContext';
import { routeOf } from '../../../utils/appRoutes';
import useModuleActions from '../../../helper/hooks/useModuleActions';
import AppSelect from '../../shared/AppSelect';

// the history belongs to the Orders module: Retry = update, Delete / Clear = delete
const ORDERS_MODULE = 'marketsync-dropshipping/orders';

const CLEANUP_API = `${API_BASE_URL}/api/sellvia-cleanup`;

// Cream / gold palette (same family as the Account / Settings pages).
const T = {
    text: '#1d1a14', sub: '#4a4234', muted: '#7d7466',
    gold: '#b8892c', goldDark: '#7a5712', ring: '#d8b25a',
    border: '#ece2cc', card: '#fffcf7', cream: '#fdf9f1',
};
const GOLD_GRADIENT = 'linear-gradient(180deg,#c99a35 0%,#8c6a1c 100%)';
const fieldSx = {
    bgcolor: '#fffdf8', borderRadius: '12px', color: T.text,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6d6b2' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.ring },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.gold, borderWidth: '1.5px' },
    '&.Mui-focused::before': { display: 'none !important' },
};

// Empty-state art — a history sheet with a gold delete badge.
const EmptyHistoryArt = () => (
    <svg width="200" height="150" viewBox="0 0 200 150" aria-hidden="true">
        <ellipse cx="100" cy="128" rx="62" ry="10" fill="rgba(184,137,44,0.10)" />
        <circle cx="100" cy="72" r="58" fill="rgba(236,208,138,0.18)" />
        <g stroke="#b8892c" strokeWidth="3.5" strokeLinecap="round">
            <line x1="38" y1="58" x2="48" y2="63" /><line x1="36" y1="78" x2="47" y2="76" />
            <line x1="152" y1="48" x2="160" y2="40" /><line x1="156" y1="66" x2="168" y2="63" />
        </g>
        <rect x="62" y="30" width="72" height="88" rx="8" fill="#fffaf0" stroke="#e6d3a6" strokeWidth="2" />
        <g stroke="#dcc28a" strokeWidth="5" strokeLinecap="round">
            <line x1="76" y1="50" x2="108" y2="50" /><line x1="76" y1="64" x2="118" y2="64" />
            <line x1="76" y1="78" x2="104" y2="78" /><line x1="76" y1="92" x2="96" y2="92" />
        </g>
        <circle cx="128" cy="98" r="21" fill="url(#goldBadge)" stroke="#fffaf0" strokeWidth="3" />
        <g fill="none" stroke="#fffaf0" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M119 91h18" /><path d="M125 91v-3h6v3" /><path d="M121 91l1.5 15h11l1.5-15" />
            <line x1="126" y1="95" x2="126" y2="102" /><line x1="130" y1="95" x2="130" y2="102" />
        </g>
        <defs>
            <linearGradient id="goldBadge" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#c99a35" /><stop offset="100%" stopColor="#8c6a1c" />
            </linearGradient>
        </defs>
    </svg>
);

// ── Day filter — fixed presets, replacing the old per-day dropdown ──
const DAY_OPTIONS = [
    { value: 'all',       label: 'All Days',      dot: '#b8892c' },
    { value: 'today',     label: 'Today',         dot: '#34d399' },
    { value: 'yesterday', label: 'Yesterday',     dot: '#38bdf8' },
    { value: 'last30',    label: 'Last 30 Days',  dot: '#fbbf24' },
];

// ── Status pill — shared look for every status field on a CleanupRecord row.
//    A row's CleanupRecord is optional now (see note above the endpoint) —
//    when absent, these pills simply don't render for that order. ──
const STATUS_COLORS = {
    success:     { bg: 'rgba(16,185,129,0.15)', color: '#10b981' },
    failed:      { bg: 'rgba(239,68,68,0.15)',  color: '#ef4444' },
    pending:     { bg: 'rgba(234,179,8,0.15)',  color: '#eab308' },
    skipped:     { bg: 'rgba(148,163,184,0.15)', color: '#94a3b8' },
    no_endpoint: { bg: 'rgba(249,115,22,0.15)', color: '#f97316' },
    not_due:     { bg: 'rgba(148,163,184,0.15)', color: '#94a3b8' },
    plugin_not_connected: { bg: 'rgba(249,115,22,0.15)', color: '#f97316' },
    not_attempted: { bg: 'rgba(148,163,184,0.15)', color: '#94a3b8' },
    requires_payment: { bg: 'rgba(239,68,68,0.15)', color: '#ef4444' },
};
const STATUS_LABELS = {
    success: 'Done', failed: 'Failed', pending: 'Pending', skipped: 'Skipped',
    no_endpoint: 'Plugin not ready', not_due: 'Not due yet',
    plugin_not_connected: 'Plugin not connected',
    not_attempted: 'Not started', requires_payment: 'Payment needed',
};
const StatusPill = ({ status }) => {
    const c = STATUS_COLORS[status] || STATUS_COLORS.pending;
    return (
        <Chip
            size="small"
            label={STATUS_LABELS[status] || status}
            sx={{ bgcolor: c.bg, color: c.color, fontWeight: 700, fontSize: 10.5, height: 20 }}
        />
    );
};

// The order's own permanent trail (order_process_history) has no separate
// status field per entry — just a message string — so its overall "how did
// this order end up" color is read off the LAST entry's leading symbol
// (✓ / ⏳ / ✗), the same convention every step message already uses.
const inferOrderTone = (history) => {
    const last = history?.[history.length - 1]?.message || '';
    if (last.startsWith('✗') || /failed|error/i.test(last)) return { color: '#ef4444', label: 'Failed' };
    if (last.startsWith('⏳') || /not connected|payment/i.test(last)) return { color: '#f59e0b', label: 'Waiting' };
    if (last.startsWith('✓')) return { color: '#10b981', label: 'Done' };
    return { color: '#60a5fa', label: 'In progress' };
};

// Sellvia's delete succeeding but WordPress not yet reflecting it is the
// exact "did the system already handle this?" ambiguity the user wants
// called out explicitly — the message is specific to WHERE in the chain the
// order actually is, not one generic sentence for every case. Only rendered
// when this order actually has a CleanupRecord (see the endpoint's note on
// why that's now optional).
const GoToOrderLink = ({ order, onGoToOrder }) => (
    <button
        type="button"
        onClick={() => onGoToOrder(order)}
        style={{
            color: '#b8892c', fontWeight: 700, background: 'none', border: 'none', padding: 0,
            font: 'inherit', cursor: 'pointer', textDecoration: 'underline',
        }}
    >
        Process this order from the Orders page →
    </button>
);

const HandledNotice = ({ order, onGoToOrder }) => {
    const record = order.cleanup_record;
    if (!record || record.sellvia_delete_status !== 'success') return null;

    if (record.wp_update_status === 'plugin_not_connected') {
        return (
            <Typography sx={{ fontSize: 10.5, color: '#b45309', mt: 0.3 }}>
                ✓ Deleted from MarketSync — ⏳ this store's WordPress plugin isn't connected, so the price update is on hold.
                It will retry automatically as soon as the plugin reconnects — no need to run this again.
            </Typography>
        );
    }
    if (record.process_order_status === 'requires_payment') {
        return (
            <Typography sx={{ fontSize: 10.5, color: '#b91c1c', mt: 0.3 }}>
                ✓ Deleted from MarketSync, ✓ price updated — ⚠ MarketSync needs payment to process this order.
                {' '}<GoToOrderLink order={order} onGoToOrder={onGoToOrder} />
            </Typography>
        );
    }
    if (record.mode === 'auto' && record.process_order_status === 'failed') {
        return (
            <Typography sx={{ fontSize: 10.5, color: '#b91c1c', mt: 0.3 }}>
                ✓ Deleted from MarketSync, ✓ price updated — ✗ processing on MarketSync failed. Retries automatically, or {' '}
                <GoToOrderLink order={order} onGoToOrder={onGoToOrder} /> to process it now.
            </Typography>
        );
    }
    if (record.mode === 'manual' && ['not_attempted', 'failed'].includes(record.process_order_status)) {
        return (
            <Typography sx={{ fontSize: 10.5, color: '#7a5712', mt: 0.3 }}>
                ✓ Deleted from MarketSync, ✓ price updated — <GoToOrderLink order={order} onGoToOrder={onGoToOrder} />
            </Typography>
        );
    }
    if (record.reprice_on_processed_status === 'success') {
        return (
            <Typography sx={{ fontSize: 10.5, color: '#15803d', mt: 0.3 }}>
                ✓ Fully complete — deleted from MarketSync, order processed, and WordPress has the final price.
            </Typography>
        );
    }
    return null;
};

// ── One order's card — id/customer/amount/product header, optional status
//    pills + retry (only when a CleanupRecord exists), and its own
//    collapsible step-by-step log with each entry's REAL saved timestamp. ──
const OrderCard = ({ order, onRetry, retrying, onDelete, onGoToOrder, expanded, onToggle }) => {
    const can = useModuleActions(ORDERS_MODULE);
    const history = order.order_process_history || [];
    const tone = inferOrderTone(history);
    const record = order.cleanup_record;
    const lastAt = history[history.length - 1]?.at;
    // Retry/refresh shows for anything failed OR still waiting (⏳ — e.g. a
    // plugin that wasn't connected yet, or a step due to resume once the
    // order itself becomes processed) — not just a Step 1/2 CleanupRecord
    // failure. Works with or without a CleanupRecord existing at all —
    // onRetry now goes through /manual/start (same as "Approve Order" on
    // the Orders page), which resumes this one order from whichever step
    // it's actually stuck on, upserting a fresh CleanupRecord if needed.
    const canRetry = tone.label === 'Failed' || tone.label === 'Waiting';

    return (
        <Box sx={{
            display: 'flex', flexWrap: { xs: 'wrap', sm: 'nowrap' }, alignItems: 'flex-start', gap: 1.5, p: 1.5, mb: 0.8, borderRadius: 2,
            bgcolor: T.card, border: `1px solid ${T.border}`,
            borderLeft: `3px solid ${tone.color}`,
            boxShadow: '0 2px 8px rgba(122,87,18,0.05)',
        }}>
            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: T.text }}>#{order.order_id}</Typography>
                    <Chip size="small" label={tone.label} sx={{ bgcolor: `${tone.color}26`, color: tone.color, fontWeight: 700, fontSize: 10, height: 18 }} />
                    {order.customer_name && <Typography sx={{ fontSize: 11, color: T.sub }}>· {order.customer_name}</Typography>}
                    {order.amount != null && <Typography sx={{ fontSize: 11, color: T.goldDark, fontWeight: 700 }}>· ${Number(order.amount).toFixed(2)}</Typography>}
                </Box>
                {order.product_title && (
                    <Typography sx={{ fontSize: 10.5, color: T.muted, mt: 0.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: { xs: 220, sm: 520 } }}>
                        {order.product_title}
                    </Typography>
                )}
                <Typography sx={{ fontSize: 10.5, color: T.muted, mt: 0.2, wordBreak: 'break-all' }}>
                    hash: {order.order_hash}{record ? ` · ${record.mode === 'auto' ? 'Auto' : 'Manual'}` : ''}
                    {lastAt && <> · last update {toUsaDayjs(lastAt).format('MMM DD, h:mm:ss A')}</>}
                </Typography>
                <HandledNotice order={order} onGoToOrder={onGoToOrder} />
                {history.length > 0 && (
                    <>
                        <button
                            type="button"
                            onClick={onToggle}
                            style={{
                                marginTop: 4, background: 'none', border: 'none', padding: 0, font: 'inherit',
                                fontSize: 10.5, color: T.gold, cursor: 'pointer', textDecoration: 'underline',
                            }}
                        >
                            {expanded ? 'Hide' : 'Show'} step-by-step details ({history.length})
                        </button>
                        {expanded && (
                            <Box sx={{ mt: 0.5, pl: 1, borderLeft: `2px solid ${T.border}` }}>
                                {history.map((entry, i) => (
                                    <Typography key={i} sx={{ fontSize: 10, color: T.sub, lineHeight: 1.7 }}>
                                        <span style={{ color: T.muted }}>{toUsaDayjs(entry.at).format('MMM DD, h:mm:ss A')}</span> — {entry.message}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                    </>
                )}
            </Box>
            {record && (
                <Box sx={{
                    display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, flexWrap: 'wrap', gap: 0.4,
                    alignItems: { xs: 'center', sm: 'flex-end' }, minWidth: { xs: 0, sm: 120 },
                    order: { xs: 3, sm: 0 }, width: { xs: '100%', sm: 'auto' },
                }}>
                    <Tooltip title="Delete from MarketSync"><span><StatusPill status={record.sellvia_delete_status} /></span></Tooltip>
                    <Tooltip title="WordPress reprice"><span><StatusPill status={record.wp_update_status} /></span></Tooltip>
                    {record.mode === 'auto' && <Tooltip title="Process order (Auto)"><span><StatusPill status={record.process_order_status} /></span></Tooltip>}
                    <Tooltip title="Reprice once processed"><span><StatusPill status={record.reprice_on_processed_status} /></span></Tooltip>
                </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {canRetry && can('update') && (
                    <Tooltip title="Retry this order">
                        <IconButton size="small" disabled={retrying} onClick={() => onRetry(order)} sx={{ color: T.goldDark }}>
                            {retrying ? <CircularProgress size={14} sx={{ color: T.gold }} /> : <ReplayIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                    </Tooltip>
                )}
                {can('delete') && (
                <Tooltip title="Delete this order's history">
                    <IconButton size="small" onClick={() => onDelete(order)} sx={{ color: '#ef4444' }}><DeleteOutlineIcon sx={{ fontSize: 17 }} /></IconButton>
                </Tooltip>
                )}
            </Box>
        </Box>
    );
};

const SellviaCleanupHistoryPanel = ({ open, onClose }) => {
    const can = useModuleActions(ORDERS_MODULE);
    const navigate = useNavigate();
    const { token: appToken } = useSelector((s) => s.auth);
    const cleanupJobCtx = useSellviaCleanupJob();
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    // Fixed presets — replaces the old per-calendar-day dropdown, which grew
    // one entry per day forever. Defaults to "Today".
    const [dayFilter, setDayFilter] = useState('today');
    const [retryingId, setRetryingId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null); // order or { bulkDay } for "delete this whole range"
    const [expandedLogId, setExpandedLogId] = useState(null); // order_id currently showing its full step-by-step log
    const [collapsedCompanies, setCollapsedCompanies] = useState({});
    const [collapsedStores, setCollapsedStores] = useState({});

    const load = useCallback(async () => {
        if (!appToken) return;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            const isSearching = search.trim().length > 0;
            // A search is meant to find an order across ANY day's history —
            // so while actively searching, the day filter is set aside
            // entirely rather than narrowing the search down further.
            params.set('day', isSearching ? 'all' : dayFilter);
            if (isSearching) params.set('search', search.trim());
            const res = await axios.get(`${CLEANUP_API}/order-history?${params}`, { headers: { Authorization: `Bearer ${appToken}` } });
            setGroups(res.data.groups || []);
        } catch (e) { console.error('[cleanup-history] load failed:', e.message); }
        finally { setLoading(false); }
    }, [appToken, search, dayFilter]);

    useEffect(() => { if (open) load(); }, [open, load]);

    // Same job (/manual/start) "Approve Order" on the Orders page uses —
    // resumes this one order from whichever step it's actually stuck on
    // (failed or still waiting), CleanupRecord or not. The job runs in the
    // background; cleanupJobCtx's own toast reports when it's actually
    // done, and the effect below re-pulls this panel's list at that point
    // so the retried order's fresh state shows without a manual Refresh click.
    const handleRetry = async (order) => {
        if (!can('update')) return;
        setRetryingId(order.order_id);
        try {
            const res = await axios.post(`${CLEANUP_API}/manual/start`, { order_ids: [order.order_id] }, { headers: { Authorization: `Bearer ${appToken}` } });
            if (res.data?.job) cleanupJobCtx?.startJob(res.data.job);
            notifySuccess(`Retrying order #${order.order_id}…`);
        } catch (e) {
            notifyError(e.response?.data?.error || 'Retry failed');
            setRetryingId(null);
        }
    };

    // Releases the "retrying" spinner and re-pulls the list once the job we
    // started for it actually finishes (not sooner) — same reasoning as the
    // Orders page's own approvingOrderId lock.
    useEffect(() => {
        if (!retryingId) return;
        const jobStatus = cleanupJobCtx?.job?.status;
        if (!jobStatus || jobStatus === 'running') return;
        setRetryingId(null);
        if (open) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [retryingId, cleanupJobCtx?.job?.status]);

    // Processing the order itself is NEVER done from History — it's always
    // the Orders page's own existing "Process order" button, untouched. This
    // just deep-links there and closes this dialog on the way out.
    const goToOrder = (order) => {
        onClose?.();
        navigate(`${routeOf("admin.orders")}?search=${encodeURIComponent(order.order_hash || order.order_id)}&date=all&expand=${encodeURIComponent(order.order_id)}`);
    };

    const handleConfirmDelete = async () => {
        if (!can('delete')) { setDeleteTarget(null); return; }
        if (!deleteTarget) return;
        try {
            if (deleteTarget.bulkDay) {
                const res = await axios.delete(`${CLEANUP_API}/order-history/day/${deleteTarget.bulkDay}`, { headers: { Authorization: `Bearer ${appToken}` } });
                notifySuccess(`Cleared history for ${res.data.deleted} order${res.data.deleted !== 1 ? 's' : ''}`);
            } else {
                await axios.delete(`${CLEANUP_API}/order-history/${deleteTarget.order_id}`, { headers: { Authorization: `Bearer ${appToken}` } });
                notifySuccess(`Order #${deleteTarget.order_id}'s history cleared`);
            }
            await load();
        } catch (e) {
            notifyError(e.response?.data?.error || 'Delete failed');
        } finally { setDeleteTarget(null); }
    };

    const totalOrders = useMemo(() => groups.reduce((sum, g) => sum + g.stores.reduce((s2, st) => s2 + st.orders.length, 0), 0), [groups]);
    const activeDayLabel = DAY_OPTIONS.find((d) => d.value === dayFilter)?.label || 'Today';

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: {
                    height: { xs: '92vh', sm: '85vh' }, m: { xs: 1.5, sm: 4 }, width: { xs: 'calc(100% - 24px)', sm: undefined },
                    borderRadius: '20px', color: T.text, overflow: 'hidden',
                    background: `linear-gradient(165deg, ${T.cream} 0%, #f8efdc 100%)`,
                    border: `1px solid #efdfbd`,
                    boxShadow: '0 0 0 5px rgba(255,255,255,0.7), 0 24px 60px rgba(122,87,18,0.18)',
                } }}
            >
                <DialogTitle sx={{ p: 0 }}>
                    <Box sx={{
                        position: 'relative', overflow: 'hidden',
                        display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2.5 },
                        px: { xs: 2, sm: 3.5 }, py: { xs: 1.8, sm: 2.4 }, pr: { xs: 7, sm: 9 },
                        background: 'linear-gradient(180deg,#fffcf6 0%,#fbf4e6 100%)',
                        borderBottom: `1px solid ${T.border}`,
                    }}>
                        {/* Gold swoosh, top-right corner */}
                        <Box component="svg" viewBox="0 0 200 110" preserveAspectRatio="none" aria-hidden="true"
                            sx={{ position: 'absolute', right: 0, top: 0, width: { xs: 140, sm: 220 }, height: '100%', pointerEvents: 'none' }}>
                            <path d="M40 0 C 110 30, 150 70, 200 80 L200 0 Z" fill="rgba(236,208,138,0.22)" />
                            <path d="M120 110 C 150 80, 175 70, 200 62 L200 110 Z" fill="url(#cleanupSwoosh)" />
                            <path d="M70 0 C 120 25, 160 40, 200 38" fill="none" stroke="rgba(216,178,90,0.55)" strokeWidth="2" />
                            <defs>
                                <linearGradient id="cleanupSwoosh" x1="0" y1="0" x2="1" y2="1">
                                    <stop offset="0%" stopColor="#d8b25a" /><stop offset="100%" stopColor="#8c6a1c" />
                                </linearGradient>
                            </defs>
                        </Box>

                        {/* MarketSync brand */}
                        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, flexShrink: 0, position: 'relative' }}>
                            <Box component="img" src="/assests/website-logo.png" alt="" sx={{ width: 46, height: 40, objectFit: 'contain' }} />
                            <Box>
                                <Typography sx={{ fontSize: '1.45rem', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.02em', color: T.text }}>
                                    Market<Box component="span" sx={{ color: T.gold }}>Sync</Box>
                                </Typography>
                                <Typography sx={{ fontSize: '0.7rem', color: T.sub, mt: 0.4, whiteSpace: 'nowrap' }}>
                                    Connect <Box component="span" sx={{ color: T.gold }}>•</Box> Manage <Box component="span" sx={{ color: T.gold }}>•</Box> Grow
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: { xs: 'none', md: 'block' }, width: '1.5px', alignSelf: 'stretch', my: 0.5, flexShrink: 0, background: 'rgba(184,137,44,0.35)' }} />

                        <Box sx={{ minWidth: 0, position: 'relative' }}>
                            <Typography sx={{ fontWeight: 800, fontSize: { xs: 16, sm: 20 }, color: T.text, letterSpacing: '-0.01em' }}>MarketSync Order Cleanup History</Typography>
                            <Typography sx={{ fontSize: { xs: 11, sm: 12 }, color: T.sub, mt: 0.3 }}>
                                Delete-from-MarketSync + WordPress reprice, straight from each order's own saved trail — {totalOrders} order{totalOrders !== 1 ? 's' : ''} · {activeDayLabel}
                            </Typography>
                        </Box>

                        <IconButton
                            onClick={onClose}
                            aria-label="Close"
                            sx={{
                                position: 'absolute', right: { xs: 12, sm: 20 }, top: '50%', transform: 'translateY(-50%)',
                                width: 42, height: 42, color: T.text, bgcolor: '#fffdf8',
                                border: `1px solid #e6d6b2`, boxShadow: '0 3px 10px rgba(122,87,18,0.12)',
                                '&:hover': { bgcolor: '#fdf1d8' },
                            }}
                        >
                            <CloseRoundedIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: { xs: 1.5, sm: 3 }, pt: { xs: '16px !important', sm: '24px !important' } }}>
                    {/* Search + day filter + refresh */}
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField
                            size="small"
                            placeholder="Search order ID, hash, or customer…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            sx={{
                                flex: '1 1 260px', minWidth: 0,
                                '& .MuiOutlinedInput-root': { ...fieldSx, height: 46 },
                                '& input::placeholder': { color: T.muted, opacity: 1 },
                            }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 20, color: T.sub }} /></InputAdornment> }}
                        />
                        <AppSelect size="small" value={dayFilter} onChange={(e) => setDayFilter(e.target.value)} fullWidth={false}
                            sx={{ minWidth: { xs: 0, sm: 210 }, flex: { xs: '1 1 140px', sm: '0 0 auto' } }}>
                            {DAY_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: opt.dot, flexShrink: 0 }} />
                                        {opt.label}
                                    </Box>
                                </MenuItem>
                            ))}
                        </AppSelect>
                        {/* Manual refetch — re-pulls the whole grouped list straight from
                            the DB for whatever day/search is currently active, without
                            needing to close and reopen the dialog. */}
                        <Button
                            size="small"
                            onClick={load}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <RefreshIcon sx={{ fontSize: '20px !important' }} />}
                            sx={{
                                color: '#fff', fontWeight: 700, fontSize: '0.95rem', textTransform: 'none', borderRadius: '12px',
                                height: 46, px: 3, flex: { xs: '0 0 auto' },
                                background: GOLD_GRADIENT, boxShadow: '0 6px 16px rgba(140,106,28,0.35)',
                                '&:hover': { background: 'linear-gradient(180deg,#d4a63e 0%,#95711f 100%)' },
                                '&.Mui-disabled': { color: 'rgba(255,255,255,0.75)', background: GOLD_GRADIENT, opacity: 0.8 },
                            }}
                        >
                            Refresh
                        </Button>
                        {totalOrders > 0 && can('delete') && (
                            <Tooltip title={`Clear history for every order shown (${activeDayLabel})`}>
                                <Button
                                    size="small"
                                    onClick={() => setDeleteTarget({ bulkDay: dayFilter })}
                                    startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                                    sx={{ color: '#b91c1c', fontWeight: 700, textTransform: 'none', borderRadius: '12px', height: 46, px: 2, bgcolor: '#fffdf8', border: '1.5px solid rgba(185,28,28,0.3)', '&:hover': { bgcolor: 'rgba(185,28,28,0.06)' } }}
                                >
                                    Clear {activeDayLabel}
                                </Button>
                            </Tooltip>
                        )}
                    </Box>

                    <Box sx={{
                        borderRadius: '16px', border: `1px solid ${T.border}`, bgcolor: 'rgba(255,253,248,0.75)',
                        minHeight: { xs: 280, sm: 'calc(100% - 72px)' }, p: { xs: 1.2, sm: 2 },
                        boxShadow: '0 4px 18px rgba(122,87,18,0.05)',
                    }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}><CircularProgress sx={{ color: T.gold }} /></Box>
                    ) : groups.length === 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', py: { xs: 5, sm: 10 }, px: 2 }}>
                            <EmptyHistoryArt />
                            <Typography sx={{ mt: 2, color: T.text, fontSize: { xs: 14, sm: 16 }, maxWidth: 640 }}>
                                {dayFilter === 'today' && !search.trim()
                                    ? <>No orders processed today yet. Pick a different range above (or search) to see older history.</>
                                    : <>No order history matches this filter.</>}
                            </Typography>
                        </Box>
                    ) : (
                        groups.map((company) => {
                            const companyOrderCount = company.stores.reduce((sum, st) => sum + st.orders.length, 0);
                            const companyCollapsed = !!collapsedCompanies[company.company_id];
                            return (
                                <Box key={company.company_id} sx={{ mb: 2.5, borderRadius: '14px', overflow: 'hidden', border: `1px solid #e6d3a6`, bgcolor: '#fffdf8' }}>
                                    <Box
                                        onClick={() => setCollapsedCompanies((p) => ({ ...p, [company.company_id]: !p[company.company_id] }))}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.2, cursor: 'pointer',
                                            background: 'linear-gradient(135deg,#f6e9cc 0%,#f0dcae 100%)',
                                        }}
                                    >
                                        <Avatar sx={{ width: 26, height: 26, background: GOLD_GRADIENT }}><BusinessIcon sx={{ fontSize: 14, color: '#fff' }} /></Avatar>
                                        <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: T.text, flex: 1, minWidth: 0 }}>
                                            {company.company_name} <span style={{ color: T.goldDark, fontWeight: 600 }}>· {companyOrderCount} order{companyOrderCount !== 1 ? 's' : ''}</span>
                                        </Typography>
                                        {companyCollapsed ? <ExpandMoreIcon sx={{ color: T.goldDark }} /> : <ExpandLessIcon sx={{ color: T.goldDark }} />}
                                    </Box>
                                    {!companyCollapsed && (
                                        <Box sx={{ p: { xs: 1, sm: 1.5 } }}>
                                            {company.stores.map((store) => {
                                                const storeKey = `${company.company_id}:${store.store_id}`;
                                                const storeCollapsed = !!collapsedStores[storeKey];
                                                return (
                                                    <Box key={store.store_id} sx={{ mb: 1.5, borderRadius: '12px', overflow: 'hidden', border: `1px solid ${T.border}` }}>
                                                        <Box
                                                            onClick={() => setCollapsedStores((p) => ({ ...p, [storeKey]: !p[storeKey] }))}
                                                            sx={{
                                                                display: 'flex', alignItems: 'center', gap: 1, px: 1.8, py: 0.9, cursor: 'pointer',
                                                                background: '#fbf3df',
                                                            }}
                                                        >
                                                            <StorefrontIcon sx={{ fontSize: 15, color: T.gold }} />
                                                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.text, flex: 1, minWidth: 0 }}>
                                                                {store.store_name} <span style={{ color: T.goldDark, fontWeight: 500 }}>· {store.orders.length} order{store.orders.length !== 1 ? 's' : ''}</span>
                                                            </Typography>
                                                            {storeCollapsed ? <ExpandMoreIcon sx={{ fontSize: 18, color: T.goldDark }} /> : <ExpandLessIcon sx={{ fontSize: 18, color: T.goldDark }} />}
                                                        </Box>
                                                        {!storeCollapsed && (
                                                            <Box sx={{ p: 1.2 }}>
                                                                {store.orders.map((order) => (
                                                                    <OrderCard
                                                                        key={order.order_id}
                                                                        order={order}
                                                                        retrying={retryingId === order.order_id}
                                                                        onRetry={handleRetry}
                                                                        onDelete={setDeleteTarget}
                                                                        onGoToOrder={goToOrder}
                                                                        expanded={expandedLogId === order.order_id}
                                                                        onToggle={() => setExpandedLogId((cur) => (cur === order.order_id ? null : order.order_id))}
                                                                    />
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })
                    )}
                    </Box>
                </DialogContent>
            </Dialog>

            <ConfirmDialog
                open={!!deleteTarget}
                severity="delete"
                title={deleteTarget?.bulkDay ? `Clear history for "${DAY_OPTIONS.find((d) => d.value === deleteTarget.bulkDay)?.label}"?` : `Clear this order's history?`}
                message={deleteTarget?.bulkDay
                    ? `This clears the saved step-by-step history for every order shown in this range. This only clears the History record — it does NOT restore anything on MarketSync or WordPress.`
                    : `This clears order #${deleteTarget?.order_id}'s saved step-by-step history back to empty. This only clears the History record — it does NOT restore anything on MarketSync or WordPress.`}
                confirmLabel="Yes, Clear"
                onConfirm={handleConfirmDelete}
                onCancel={() => setDeleteTarget(null)}
            />
        </>
    );
};

export default SellviaCleanupHistoryPanel;
